import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACTIVITY_PRIORITIES, isActivityTypeAllowedForRole, type ActivityPriority, type ActivityType, type WorkSprint } from "@torre/work-hours";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { useTeamMembersLite } from "../hooks/useActivityData";
import { activityErrorMessage, createActivity, updateActivity, type ActivityPayload, type ActivityRow } from "../services/activitiesService";
import { hoursToMinutes } from "../lib/format";

// ---------------------------------------------------------------------------
// Alta y edición de un ticket (épica #1299). El tipo sale SOLO del catálogo
// activo y, si hay persona asignada, solo de lo permitido para su puesto: la
// restricción por puesto se ve aquí y la vuelve a aplicar el servidor.
// ---------------------------------------------------------------------------

interface Props {
  mode: "create" | "edit";
  activity?: ActivityRow | null;
  catalog: readonly ActivityType[];
  sprints: readonly WorkSprint[];
  /** Épicas (milestones y tickets padre) disponibles como padre. */
  epics: readonly ActivityRow[];
  /** Sprint preseleccionado al crear (p. ej. desde el tablero). */
  defaultSprintId?: string | null;
  onClose: () => void;
  onSaved: (activity: ActivityRow, created: boolean) => void;
}

const minutesToHoursText = (m: number | null | undefined) => (m == null ? "" : String(Math.round((m / 60) * 100) / 100));

export default function ActividadFormModal({ mode, activity, catalog, sprints, epics, defaultSprintId = null, onClose, onSaved }: Props) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const { members } = useTeamMembersLite();

  const [title, setTitle] = useState(activity?.title ?? "");
  const [typeKey, setTypeKey] = useState(activity?.typeKey ?? "");
  const [assignee, setAssignee] = useState(activity?.assigneeTeamMemberId ?? "");
  const [sprintId, setSprintId] = useState(activity?.sprintId ?? defaultSprintId ?? "");
  const [priority, setPriority] = useState<ActivityPriority>(activity?.priority ?? "medium");
  const [points, setPoints] = useState(activity?.storyPoints == null ? "" : String(activity.storyPoints));
  const [hours, setHours] = useState(minutesToHoursText(activity?.estimateMinutes));
  const [startDate, setStartDate] = useState(activity?.startDate ?? "");
  const [dueDate, setDueDate] = useState(activity?.dueDate ?? "");
  const [parentId, setParentId] = useState(activity?.parentId ?? "");
  const [description, setDescription] = useState(activity?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assigneeRole = useMemo(() => members.find((m) => m.id === assignee)?.role ?? null, [members, assignee]);
  // Tipos ofrecidos: activos y, con asignado, permitidos para su puesto. El tipo
  // actual del ticket se conserva en la lista aunque ya no aplique, para no
  // cambiarlo a escondidas al editar.
  const types = useMemo(
    () => catalog.filter((ty) => ty.active && (!assignee || isActivityTypeAllowedForRole(ty, assigneeRole) || ty.key === activity?.typeKey)),
    [catalog, assignee, assigneeRole, activity?.typeKey],
  );
  const typeNotAllowed = Boolean(assignee && typeKey && catalog.some((ty) => ty.key === typeKey && !isActivityTypeAllowedForRole(ty, assigneeRole)));

  useEffect(() => {
    if (!typeKey && types.length && mode === "create") setTypeKey(types.find((ty) => ty.key === "meeting")?.key ?? types[0].key);
  }, [types, typeKey, mode]);

  const openSprints = sprints.filter((s) => s.status !== "closed" || s.id === activity?.sprintId);

  const submit = async () => {
    setError(null);
    if (title.trim().length < 3) return setError(t("staffActivities.form.errors.title"));
    if (!typeKey) return setError(t("staffActivities.form.errors.type"));
    if (typeNotAllowed) return setError(t("staffActivities.form.typeNotAllowed"));
    if (startDate && dueDate && startDate > dueDate) return setError(t("staffActivities.form.errors.dates"));
    const payload: ActivityPayload = {
      title: title.trim(),
      typeKey,
      description: description.trim() || null,
      assigneeTeamMemberId: assignee || null,
      sprintId: sprintId || null,
      priority,
      storyPoints: points === "" ? null : Math.round(Number(points) * 2) / 2,
      estimateMinutes: hours === "" ? null : hoursToMinutes(hours),
      startDate: startDate || null,
      dueDate: dueDate || null,
      parentId: parentId || null,
    };
    setSaving(true);
    try {
      if (mode === "edit" && activity) {
        const saved = await updateActivity(activity.id, payload);
        onSaved(saved ?? { ...activity, ...payload } as ActivityRow, false);
      } else {
        const res = await createActivity(payload);
        onSaved(res.data, res.created);
      }
    } catch (err) {
      setError(activityErrorMessage(err) ?? t("staffActivities.form.errors.save"));
    } finally {
      setSaving(false);
    }
  };

  const field = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;
  const label = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  return (
    <Modal
      title={t(mode === "edit" ? "staffActivities.form.editTitle" : "staffActivities.form.createTitle")}
      themeClasses={tc}
      onClose={() => !saving && onClose()}
      width="max-w-2xl"
      content={
        <div className="space-y-4" data-testid="activity-form">
          <div>
            <label className={label}>{t("staffActivities.form.title")} *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("staffActivities.form.titlePlaceholder")} className={field} aria-label={t("staffActivities.form.title")} />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>{t("staffActivities.form.type")} *</label>
              <select value={typeKey} onChange={(e) => setTypeKey(e.target.value)} className={field} aria-label={t("staffActivities.form.type")}>
                <option value="">—</option>
                {types.map((ty) => (
                  <option key={ty.key} value={ty.key}>
                    {ty.name}
                  </option>
                ))}
              </select>
              <p className={`mt-1 text-[11px] ${typeNotAllowed ? "text-red-500" : tc.textSecondary}`}>{typeNotAllowed ? t("staffActivities.form.typeNotAllowed") : t("staffActivities.form.typeHint")}</p>
            </div>
            <div>
              <label className={label}>{t("staffActivities.form.assignee")}</label>
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className={field} aria-label={t("staffActivities.form.assignee")}>
                <option value="">{t("staffActivities.form.noAssignee")}</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {m.role ? ` · ${t(`team.roles.${m.role}`, { defaultValue: m.role })}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>{t("staffActivities.form.sprint")}</label>
              <select value={sprintId} onChange={(e) => setSprintId(e.target.value)} className={field} aria-label={t("staffActivities.form.sprint")}>
                <option value="">{t("staffActivities.form.noSprint")}</option>
                {openSprints.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.startDate} → {s.endDate})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>{t("staffActivities.form.priority")}</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value as ActivityPriority)} className={field} aria-label={t("staffActivities.form.priority")}>
                {ACTIVITY_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {t(`staffActivities.priority.${p}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>{t("staffActivities.form.points")}</label>
              <input type="number" min={0} max={100} step={0.5} value={points} onChange={(e) => setPoints(e.target.value)} className={field} aria-label={t("staffActivities.form.points")} />
            </div>
            <div>
              <label className={label}>{t("staffActivities.form.estimateHours")}</label>
              <input type="number" min={0} step={0.25} value={hours} onChange={(e) => setHours(e.target.value)} className={field} aria-label={t("staffActivities.form.estimateHours")} />
            </div>
            <div>
              <label className={label}>{t("staffActivities.form.startDate")}</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={field} aria-label={t("staffActivities.form.startDate")} />
            </div>
            <div>
              <label className={label}>{t("staffActivities.form.dueDate")}</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={field} aria-label={t("staffActivities.form.dueDate")} />
            </div>
            <div className="sm:col-span-2">
              <label className={label}>{t("staffActivities.form.parent")}</label>
              <select value={parentId} onChange={(e) => setParentId(e.target.value)} className={field} aria-label={t("staffActivities.form.parent")}>
                <option value="">{t("staffActivities.form.noParent")}</option>
                {epics
                  .filter((e) => e.id !== activity?.id)
                  .map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.key} · {e.title}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div>
            <label className={label}>{t("staffActivities.form.description")}</label>
            <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} className={`${field} resize-y`} aria-label={t("staffActivities.form.description")} />
          </div>
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500" data-testid="activity-form-error">
              <i className="ri-error-warning-line mr-1" />
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 border-t pt-4 dark:border-dark-border">
            <button type="button" onClick={onClose} disabled={saving} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text} ${tc.buttonHover}`}>
              {t("staffActivities.form.cancel")}
            </button>
            <button type="button" onClick={() => void submit()} disabled={saving} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-60">
              {saving ? t("staffActivities.form.saving") : t(mode === "edit" ? "staffActivities.form.save" : "staffActivities.form.create")}
            </button>
          </div>
        </div>
      }
    />
  );
}
